import { Model, Web3DViewer } from '@technology/web3d';
import { SingleCapabilityProviderBase } from '../base/single-provider-base';
import {
  FeaturesServiceOptions,
  FeaturesServicePlugin,
  FeaturesServiceSceneProjection,
  FeaturesServiceModel,
  type LayerItem,
} from '@trimble/web3d-plugin-features-service';
import { getSceneProjectionFromCsib } from '../map/tile-map-provider.helpers';
import { getVerticalTglDefFromCsib } from './vertical-tgl-def';

export interface FeaturesServiceCapabilityProviderOptions {
  readonly csib: string;
  readonly geoDataFiles?: Map<string, ArrayBuffer>;
  readonly layerItems?: Promise<LayerItem[]>;
}

export class FeaturesServiceCapabilityProvider extends SingleCapabilityProviderBase<Model> {
  public idKey = 'modelId' as const;

  public async cleanup(): Promise<void> {
    await Promise.all(this.items().map((m) => this.unloadModel(m[this.idKey])));
  }

  private get plugin(): FeaturesServicePlugin {
    const plugin = this.viewer.plugins.get(FeaturesServicePlugin);
    if (!plugin) {
      throw new Error('FeaturesServicePlugin is not available on the viewer');
    }
    return plugin;
  }

  private readonly isReady: Promise<void> | null = null;
  private layerItems: LayerItem[] | undefined;

  constructor(
    viewer: Web3DViewer,
    options: FeaturesServiceCapabilityProviderOptions
  ) {
    super(viewer);

    const projectionReady = getSceneProjectionFromCsib(
      options.csib,
      options.geoDataFiles
    ).then((projection) =>
      this.setSceneProjection({
        ...projection,
        verticalTglDef: getVerticalTglDefFromCsib(options.csib),
        geoDataFiles: options.geoDataFiles,
      })
    );

    const layerItemsReady = options.layerItems ?? Promise.resolve(undefined);

    this.isReady = Promise.all([projectionReady, layerItemsReady]).then(
      ([, layerItems]) => {
        this.layerItems = layerItems;
      }
    );
  }

  /** Resolves once the scene projection has been applied to the plugin. */
  public async whenReady(): Promise<void> {
    await this.isReady;
  }

  public async setSceneProjection(
    projectionOptions: FeaturesServiceSceneProjection & {
      geoDataFiles?: Map<string, ArrayBuffer>;
    }
  ): Promise<void> {
    if (projectionOptions.tglDef && 'geoDataFiles' in projectionOptions) {
      this.plugin.setSceneProjection(projectionOptions);
      await this.plugin.initTglTransforms(projectionOptions.geoDataFiles);
    } else {
      this.plugin.setSceneProjection(projectionOptions);
    }
  }

  public async loadModel(
    options: FeaturesServiceOptions
  ): Promise<FeaturesServiceModel | null> {
    await this.isReady;
    const currModel = this.viewer.getModel<FeaturesServiceModel>(
      options.modelId
    );
    if (currModel) {
      return currModel;
    }

    const model = this.plugin.load({
      ...options,
      layerItems: options.layerItems ?? this.layerItems,
    });

    if (!this.addItem(model)) {
      throw new Error('Features Service model already added!');
    }

    return model;
  }

  public getTileCoordsFromLatLon(
    latDeg: number,
    lonDeg: number,
    zoom: number
  ): { x: number; y: number } {
    return this.plugin.getTileCoordinateFromCartographic(
      { x: lonDeg, y: latDeg },
      zoom,
      true
    );
  }

  public setVisible(modelId: string, visible: boolean): void {
    const model = this.viewer.getModel(modelId);
    if (model && 'root' in model) {
      (model.root as { visible: boolean }).visible = visible;
      this.viewer.waitForRender();
    }
  }

  // TODO: Layer Management
  public setLayerVisible(
    modelId: string,
    layerId: string,
    visible: boolean
  ): void {
    const model = this.viewer.getModel<FeaturesServiceModel>(modelId);
    if (!model) return;
    model.setLayerVisible(layerId, visible);
    this.viewer.waitForRender();
  }

  public setJobVisible(modelId: string, jobId: string, visible: boolean): void {
    const model = this.viewer.getModel<FeaturesServiceModel>(modelId);
    if (!model) return;
    model.setJobVisible(jobId, visible);
    this.viewer.waitForRender();
  }

  public async fitJobToView(modelId: string, jobId: string): Promise<void> {
    const model = this.viewer.getModel<FeaturesServiceModel>(modelId);
    const box = model?.getJobBoundingBox(jobId);
    if (!box) {
      return;
    }

    await this.viewer.camera.fitToView({ box });
  }

  public invalidateTileData(modelId: string): void {
    const model = this.viewer.getModel<FeaturesServiceModel>(modelId);
    if (!model) return;
    model.invalidateTileData();
    this.viewer.waitForRender();
  }

  public setLayerColor(
    modelId: string,
    layerId: string,
    colorHex: string
  ): boolean {
    const model = this.viewer.getModel<FeaturesServiceModel>(modelId);
    if (!model) return false;
    const applied = model.setLayerColor(layerId, colorHex);
    this.viewer.waitForRender();
    return applied;
  }

  public async unloadModel(id: Model[typeof this.idKey]): Promise<void> {
    const model = this.viewer.getModel(id);

    if (model != null) {
      await this.viewer.unload(model.modelId, {
        fitToView: false,
      });
      this.removeItem(model);
    }
  }
}
